import { ArrowLeftIcon, ArrowRightIcon } from '@chakra-ui/icons'
import { Button, Container, Flex, Spinner } from '@chakra-ui/react'
import { handlePrefetchError } from '@stocker/codegen/helpers'
import { useNewsPagesQuery, useNewsQuery } from '@stocker/codegen/pimcore'
import type { IBreadcrumbsProps } from '@stocker/ui-components/design-system'
import { HeroSimple, NextLink, Seo } from '@stocker/ui-components/design-system'
import { getNewsBlocks, getServerData, prepareNewsResponse } from '@stocker/ui-components/helpers'
import { QueryClient, dehydrate } from '@tanstack/react-query'
import type {
  GetStaticPaths,
  GetStaticPathsResult,
  GetStaticProps,
  InferGetStaticPropsType,
  NextPage,
} from 'next'
import { useRouter } from 'next/router'
import type { ParsedUrlQuery } from 'querystring'
import { useIntl } from 'react-intl'

interface IStaticParams extends ParsedUrlQuery {
  slug?: string[]
}

export const getStaticPaths: GetStaticPaths<IStaticParams> = async ({ locales = ['de', 'en'] }) => {
  const paths = await Promise.all(
    locales.map(async (locale): Promise<GetStaticPathsResult<IStaticParams>['paths']> => {
      const newsPages = await useNewsPagesQuery.fetcher({ defaultLanguage: locale })()
      const slugs =
        newsPages.getNewsPageListing?.edges
          ?.map((edge) => edge?.node?.absolutePath)
          .filter((path): path is string => typeof path === 'string') ?? []

      return slugs.map((slug) => ({
        params: {
          slug: slug.split('/').slice(1),
        },
        locale,
      }))
    }),
  )

  return {
    paths: paths.flat(),
    fallback: 'blocking',
  }
}

interface IStaticPropsParams {
  preview: boolean
  revalidateTime: number
}

export const getStaticProps: GetStaticProps<IStaticPropsParams, IStaticParams> = async ({
  params,
  preview,
  locale = 'de',
}) => {
  const serverData = await getServerData()

  const queryClient = preview
    ? new QueryClient({ defaultOptions: { queries: { cacheTime: 0 } } })
    : new QueryClient()
  const variables = { lang: locale, path: `/${params?.slug?.join('/') ?? ''}` }

  if (preview) {
    queryClient.invalidateQueries(useNewsQuery.getKey(variables))
  }

  try {
    const data = await useNewsQuery.fetcher(variables)()
    queryClient.setQueryData(useNewsQuery.getKey(variables), data)
  } catch (error) {
    return handlePrefetchError<IStaticPropsParams & IStaticParams>(error)
  }

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      preview: !!preview,
      revalidateTime: Date.now(),
      ...serverData,
    },
    revalidate: 30,
  }
}

const NewsPage: NextPage<InferGetStaticPropsType<typeof getStaticProps>> = () => {
  const { query: params, locale } = useRouter()
  const { data, isLoading, error } = useNewsQuery(
    { lang: locale ?? 'de', path: `/${(params.slug as string[] | undefined)?.join('/') ?? ''}` },
    {
      staleTime: 1000 * 60 * 10,
    },
  )

  // Add these lines to fetch previous and next news slugs
  const { data: prevNextData } = useNewsPagesQuery({ defaultLanguage: locale ?? 'de' })
  const currentIndex = prevNextData?.getNewsPageListing?.edges?.findIndex(
    (edge) =>
      edge?.node?.absolutePath === `/${(params.slug as string[] | undefined)?.join('/') ?? ''}`,
  )
  const previousNewsSlug =
    currentIndex !== undefined && currentIndex > 0
      ? prevNextData?.getNewsPageListing?.edges?.[currentIndex - 1]?.node?.absolutePath
      : undefined
  const nextNewsSlug =
    currentIndex !== undefined &&
    currentIndex < (prevNextData?.getNewsPageListing?.edges?.length ?? 0) - 1
      ? prevNextData?.getNewsPageListing?.edges?.[currentIndex + 1]?.node?.absolutePath
      : undefined

  const { formatMessage } = useIntl()

  const breadcrumbs: IBreadcrumbsProps = {
    data: [
      {
        label: formatMessage({ id: '--home' }),
        slug: '/',
      },
      {
        label: formatMessage({ id: '--news' }),
        slug: '/neuigkeiten',
      },
      {
        label: formatMessage({ id: '--article' }),
      },
    ],
  }

  if (!data && isLoading) return <Spinner />
  if (error || !data) return null

  const { seo, header, headline, navigation } = prepareNewsResponse(
    data,
    previousNewsSlug ?? undefined,
    nextNewsSlug ?? undefined,
  )

  return (
    <>
      <Seo
        // here we remove the _ from the headline that are normally used for the red headline format so they dont appear in the page header
        title={seo.title ? seo.title : headline.replace('_', '')}
        description={seo.description}
        canonicals={seo.canonicals}
        ogType="article"
      />
      {header?.image && (
        <HeroSimple image={header.image} headline={header.title} colorAccent={header.titleColor} />
      )}
      <Container maxW="container.content" pt="30px" pb="60px">
        {getNewsBlocks(data)}
        {/* Add navigation buttons */}
        <Flex justifyContent="space-between" mt={8}>
          <Button
            colorScheme='accent'
            leftIcon={<ArrowLeftIcon />}
            as={NextLink}
            href={navigation.previousNewsSlug ? `/news${navigation.previousNewsSlug}` : '#'}
            isDisabled={!navigation.previousNewsSlug}
          >
            {formatMessage({ id: '--previous-news' })}
          </Button>
          <Button
            colorScheme='accent'
            rightIcon={<ArrowRightIcon />}
            as={NextLink}
            href={navigation.nextNewsSlug ? `/news${navigation.nextNewsSlug}` : '#'}
            isDisabled={!navigation.nextNewsSlug}
          >
            {formatMessage({ id: '--next-news' })}
          </Button>
        </Flex>
      </Container>
    </>
  )
}

export default NewsPage
